import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';

const features = [
  {
    icon: <ThermostatIcon sx={{ fontSize: 22 }} />,
    title: 'Real-Time Monitoring',
    description:
      'Live temperature readings from sensors deployed across your site, updated continuously and accessible from any device.',
  },
  {
    icon: <BarChartIcon sx={{ fontSize: 22 }} />,
    title: 'Data Insights & Analytics',
    description:
      'Visualise trends, identify anomalies, and generate compliance-ready reports directly from your cloud dashboard.',
  },
  {
    icon: <ShieldOutlinedIcon sx={{ fontSize: 22 }} />,
    title: 'Compliance Ready',
    description:
      'Designed around HSG274 and HTM 04-01 standards. Automated audit trails keep your records accurate and inspection-ready.',
  },
  {
    icon: <RouterOutlinedIcon sx={{ fontSize: 22 }} />,
    title: 'Wireless IoT Sensors',
    description:
      'Compact, battery-powered sensors with long-range wireless connectivity. Simple to install - no cabling required.',
  },
  {
    icon: <NotificationsNoneIcon sx={{ fontSize: 22 }} />,
    title: 'Instant Alerts',
    description:
      'Configurable threshold alerts via email. Respond to out-of-spec readings before they become incidents.',
  },
  {
    icon: <CloudOutlinedIcon sx={{ fontSize: 22 }} />,
    title: 'Cloud Platform',
    description:
      'All data is securely stored and accessible via our hosted dashboard - no on-site servers or IT infrastructure needed.',
  },
];

const FeaturesSection = () => {
  return (
    <Box
      id="features"
      sx={{ py: { xs: 10, md: 16 }, backgroundColor: '#ffffff' }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'flex-end' },
            justifyContent: 'space-between',
            gap: 3,
            mb: { xs: 8, md: 12 },
          }}
        >
          <Box>
            <Typography
              component="span"
              sx={{
                fontFamily: 'Outfit',
                fontWeight: 500,
                fontSize: '0.78rem',
                color: '#004D40',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                display: 'block',
                mb: 1.5,
              }}
            >
              Platform Capabilities
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: 'Outfit',
                fontWeight: 800,
                fontSize: { xs: '2.2rem', md: '3rem' },
                color: '#000000',
                letterSpacing: '-0.03em',
                lineHeight: 1.05,
                maxWidth: 480,
              }}
            >
              Everything you need to monitor, analyse &amp; comply
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 400,
              fontSize: '1rem',
              color: '#777777',
              maxWidth: 340,
              lineHeight: 1.75,
              flexShrink: 0,
            }}
          >
            An end-to-end IoT solution built for facilities teams and compliance managers.
          </Typography>
        </Box>

        {/* Grid - alternating border accent */}
        <Grid container spacing={0}>
          {features.map((feature, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
              <Box
                sx={{
                  p: { xs: 4, md: 5 },
                  height: '100%',
                  borderTop: '1px solid #efefef',
                  borderRight: {
                    xs: 'none',
                    sm: index % 2 === 0 ? '1px solid #efefef' : 'none',
                    md: (index + 1) % 3 !== 0 ? '1px solid #efefef' : 'none',
                  },
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'background-color 0.25s',
                  '&:hover': { backgroundColor: '#fafafa' },
                  '&:hover .feature-accent': { opacity: 1 },
                }}
              >
                {/* Teal corner accent on hover */}
                <Box
                  className="feature-accent"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 3,
                    height: '100%',
                    backgroundColor: '#004D40',
                    opacity: 0,
                    transition: 'opacity 0.25s',
                  }}
                />
                {/* Icon chip */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    backgroundColor: 'rgba(0,77,64,0.08)',
                    color: '#004D40',
                    mb: 3,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Outfit',
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: '#000000',
                    mb: 1.25,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Outfit',
                    fontWeight: 400,
                    fontSize: '0.9rem',
                    color: '#777777',
                    lineHeight: 1.8,
                  }}
                >
                  {feature.description}
                </Typography>
              </Box>
            </Grid>
          ))}
          {/* Bottom border row */}
          <Grid size={12}>
            <Box sx={{ borderTop: '1px solid #efefef' }} />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default FeaturesSection;